import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import type { CourseView } from "@/hooks/useCourses";
import { motion, AnimatePresence } from "motion/react";
import type { CourseLevel } from "./LearningRoadmap";

interface CourseFilterProps {
  activeLevel: CourseLevel;
  courses: CourseView[];
}

const typeFilters = [
  { key: "all", label: "전체" },
  { key: "무료", label: "무료강의" },
  { key: "유료", label: "유료강의" },
  { key: "실전", label: "실전반" },
  { key: "오프라인", label: "오프라인 특강" },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

export default function CourseFilter({ activeLevel, courses }: CourseFilterProps) {
  const [activeType, setActiveType] = useState("all");
  const [sortBy, setSortBy] = useState("recommend");

  const filtered = useMemo(() => {
    let items = [...courses];
    if (activeLevel !== "all") {
      items = items.filter((c) => c.level === activeLevel);
    }
    if (activeType !== "all") {
      items = items.filter((c) => c.type === activeType);
    }
    switch (sortBy) {
      case "popular":
        items.sort((a, b) => b.students - a.students);
        break;
      case "priceLow":
        items.sort((a, b) => a.price - b.price);
        break;
      case "priceHigh":
        items.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }
    return items;
  }, [activeLevel, activeType, sortBy]);

  return (
    <div>
      {/* Type Filter Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4, ease: "easeOut" as const }}
        className="flex items-center gap-2 mb-6 overflow-x-auto pb-2"
      >
        {typeFilters.map((f) => (
          <motion.button
            key={f.key}
            onClick={() => setActiveType(f.key)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap ${
              activeType === f.key
                ? "bg-[#1C1917] text-white font-medium"
                : "bg-white border border-[#E8E5E0] text-[#5C5650] hover:border-[#C4956A] hover:text-[#C4956A]"
            }`}
          >
            {f.label}
          </motion.button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-[#8C8780] whitespace-nowrap">정렬</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm border border-[#E8E5E0] rounded-lg bg-white text-[#5C5650] focus:outline-none focus:border-[#C4956A] cursor-pointer"
          >
            <option value="recommend">추천순</option>
            <option value="popular">수강생 많은순</option>
            <option value="priceLow">가격 낮은순</option>
            <option value="priceHigh">가격 높은순</option>
          </select>
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="text-xs text-[#8C8780] mb-5"
      >
        총 <strong className="text-[#1C1917]">{filtered.length}</strong>개의
        교육과정
      </motion.p>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        key={activeLevel + activeType}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        <AnimatePresence mode="wait">
          {filtered.map((course) => (
            <motion.div
              key={course.id}
              variants={cardVariants}
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
              layout
            >
              <Link
                to={`/courses/${course.id}`}
                className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all block h-full flex flex-col"
              >
                {/* Thumbnail */}
                <div className="relative h-48 overflow-hidden">
                  <img loading="lazy" decoding="async"
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span
                      className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                        course.type === "무료"
                          ? "bg-emerald-500 text-white"
                          : course.type === "실전"
                          ? "bg-rose-500 text-white"
                          : course.type === "오프라인"
                          ? "bg-violet-500 text-white"
                          : "bg-[#C4956A] text-white"
                      }`}
                    >
                      {course.type}
                    </span>
                  </div>
                  {course.type !== "무료" && (
                    <div className="absolute bottom-3 right-3">
                      <span className="px-3 py-1 text-sm font-bold bg-white/90 rounded-full text-[#1C1917]">
                        {course.price.toLocaleString()}원
                      </span>
                    </div>
                  )}
                  {course.type === "무료" && (
                    <div className="absolute bottom-3 right-3">
                      <span className="px-3 py-1 text-sm font-bold bg-emerald-500 text-white rounded-full">
                        무료
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 flex-1 flex flex-col">
                  {/* Hybrid Learning Tags */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {course.learningMode.map((mode) => (
                      <span
                        key={mode}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${
                          mode === "온라인 VOD"
                            ? "bg-blue-50 border-blue-200 text-blue-600"
                            : "bg-emerald-50 border-emerald-200 text-emerald-600"
                        }`}
                      >
                        {mode}
                      </span>
                    ))}
                    <span className="text-[10px] text-[#8C8780] ml-0.5">
                      {course.learningSummary}
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-[#1C1917] mb-2 line-clamp-2 leading-snug">
                    {course.title}
                  </h3>
                  <p className="text-sm text-[#8C8780] mb-4 line-clamp-2 leading-relaxed">
                    {course.description}
                  </p>

                  {/* Learning Mode Summary Table */}
                  <div className="mb-4 p-3 rounded-lg bg-[#FAFAF8] border border-[#F0EDE8]">
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-computer-line text-[10px] text-[#8C8780]"></i>
                        <span className="text-[10px] text-[#5C5650]">온라인</span>
                        <span className="text-[10px] font-medium text-[#1C1917] ml-auto">
                          {course.learningMode.includes("온라인 VOD") ? "VOD" : "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-building-line text-[10px] text-[#8C8780]"></i>
                        <span className="text-[10px] text-[#5C5650]">오프라인</span>
                        <span className="text-[10px] font-medium text-[#1C1917] ml-auto">
                          {course.learningMode.includes("오프라인 현장실습") ? "현장실습" : "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-bar-chart-line text-[10px] text-[#8C8780]"></i>
                        <span className="text-[10px] text-[#5C5650]">난이도</span>
                        <span className="text-[10px] font-medium text-[#1C1917] ml-auto">
                          {course.level}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-time-line text-[10px] text-[#8C8780]"></i>
                        <span className="text-[10px] text-[#5C5650]">분량</span>
                        <span className="text-[10px] font-medium text-[#1C1917] ml-auto">
                          {course.duration}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1"></div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-[#8C8780] pt-3 border-t border-[#F0EDE8]">
                    <span className="flex items-center gap-1">
                      <i className="ri-video-line"></i>
                      {course.lectureCount}강 · {course.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {course.students.toLocaleString()}명
                    </span>
                  </div>

                  {/* CTA Button */}
                  <div className="mt-3 pt-3 border-t border-[#F0EDE8]">
                    {course.type === "무료" ? (
                      <span className="block w-full text-center py-2.5 text-xs font-semibold rounded-full bg-emerald-500 text-white whitespace-nowrap group-hover:bg-emerald-600 transition-colors">
                        무료 수강 신청하기
                      </span>
                    ) : (
                      <span className="block w-full text-center py-2.5 text-xs font-semibold rounded-full bg-[#C4956A] text-white whitespace-nowrap group-hover:bg-[#B08050] transition-colors">
                        보증금 안 날리는 진짜 경매 수업 신청하기
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {filtered.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center py-20"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
            <i className="ri-book-open-line text-2xl text-[#C4956A]"></i>
          </div>
          <p className="text-[#5C5650] font-medium mb-1">
            해당하는 과정이 없습니다
          </p>
          <p className="text-sm text-[#8C8780]">
            필터를 변경하여 다른 교육과정을 찾아보세요
          </p>
        </motion.div>
      )}
    </div>
  );
}